import { AlertIcon, CheckIcon, CopyIcon, InboxIcon, LockIcon, type Icon as OcticonIcon } from "@primer/octicons-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { HttpError, rp } from "../api/client";
import { cn } from "../lib/utils";
import { short, timeAgo } from "./format";
import { BeeMascot, BeeMascotMini, type BeeState } from "./BeeMascot";
import { Alert, AlertTitle } from "./ui/alert";
import type { Tone } from "./ui/badge";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Tooltip } from "./ui/tooltip";

export function Sha({ repo, sha, n = 10 }: { repo: string; sha: string | null | undefined; n?: number }) {
  if (!sha) return null;
  return (
    <Tooltip content={<span className="font-mono">{sha}</span>}>
      <Link className="rounded-md px-1 font-mono text-xs text-muted-foreground hover:bg-neutral-muted hover:text-foreground hover:no-underline" to={`${rp(repo)}/commit/${sha}`}>
        {short(sha, n)}
      </Link>
    </Tooltip>
  );
}

export function CopyButton({ value, title = "Copy" }: { value: string; title?: string }) {
  const [done, setDone] = useState(false);
  const [hover, setHover] = useState(false);
  const copy = () =>
    navigator.clipboard?.writeText(value).then(() => {
      setDone(true);
      setTimeout(() => setDone(false), 1200);
    });
  return (
    <Tooltip content={done ? "Copied!" : title} open={hover || done} onOpenChange={setHover}>
      <Button type="button" variant="ghost" size="icon-sm" className={cn(done && "text-success hover:text-success")} onClick={copy} aria-label={title}>
        {done ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
      </Button>
    </Tooltip>
  );
}

/** A one-line value in a gray field with a copy button, like GitHub's clone URL box. */
export function CopyField({ value, title, className }: { value: string; title?: string; className?: string }) {
  return (
    <div className={cn("flex h-8 items-center gap-1 rounded-md border bg-muted pl-2 pr-1", className)}>
      <code className="min-w-0 flex-1 truncate">{value}</code>
      <CopyButton value={value} title={title} />
    </div>
  );
}

export function TimeAgo({ ms }: { ms: number | null | undefined }) {
  if (!ms) return null;
  return (
    <Tooltip content={new Date(ms).toLocaleString()}>
      <time dateTime={new Date(ms).toISOString()}>{timeAgo(ms)}</time>
    </Tooltip>
  );
}

const DOT: Record<Tone, string> = {
  default: "bg-faint",
  green: "bg-success",
  orange: "bg-warning",
  yellow: "bg-warning",
  red: "bg-danger",
  blue: "bg-info",
  purple: "bg-merged",
};

export function LiveDot({ tone = "default" }: { tone?: Tone }) {
  return <span className={cn("inline-block size-2 shrink-0 rounded-full", DOT[tone])} />;
}

/** A branch or ref name in a blue monospace pill. */
export function RefPill({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("inline-flex items-center gap-1 rounded-md bg-info-subtle px-1.5 font-mono text-xs leading-5 text-info", className)}>{children}</span>;
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** GitHub-style identicon: a mirrored 5×5 pattern in one hashed color. Agents and bots get square corners, as apps do on GitHub, and the bee bee on their color once there is room for it (20px and up). */
export function Avatar({ name, size = 20, agent }: { name: string; size?: number; agent?: boolean }) {
  const h = hash(name);
  const cells: Array<[number, number]> = [];
  for (let i = 0; i < 15; i++) {
    if ((h >> i) & 1) {
      const x = Math.floor(i / 5);
      const y = i % 5;
      cells.push([x, y]);
      if (x < 2) cells.push([4 - x, y]);
    }
  }
  const color = `hsl(${(h >>> 16) % 360} 50% 55%)`;
  const isAgent = agent ?? /agent|bot|runner|bridge/i.test(name);
  return (
    <span
      className={cn("inline-block shrink-0 overflow-hidden border border-border-muted bg-muted align-middle", isAgent ? "rounded-md" : "rounded-full")}
      title={name}
      style={{ width: size, height: size }}
    >
      {isAgent && size >= 20 ? (
        <span className="grid size-full place-items-center" style={{ background: color }}>
          <BeeMascotMini cell={Math.max(1, Math.floor(size / 24))} />
        </span>
      ) : (
        <svg viewBox="-0.5 -0.5 6 6" aria-hidden="true" className="block size-full">
          {cells.map(([x, y]) => (
            <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill={color} />
          ))}
        </svg>
      )}
    </span>
  );
}

export function Who({ name, size, agent }: { name: string; size?: number; agent?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
      <Avatar name={name} size={size} agent={agent} /> {name}
    </span>
  );
}

/** The bee logo: a one-colour pixel bee (the mascot sprite, silhouette plus eyes) cut out of a solid disc.
 * Keep `web/index.html`'s favicon in sync with it. */
export function BeeLogo({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" shapeRendering="crispEdges" aria-hidden="true">
      <circle cx="50" cy="50" r="48" fill="currentColor" />
      <path transform="translate(12.5 19.5) scale(2.9)" className="fill-background" d="M7 0h2v1h-2zM17 0h2v1h-2zM9 1h2v1h-2zM15 1h2v1h-2zM10 2h2v1h-2zM14 2h2v1h-2zM3 3h3v1h-3zM9 3h8v1h-8zM20 3h3v1h-3zM2 4h3v1h-3zM7 4h12v1h-12zM21 4h3v1h-3zM1 5h3v1h-3zM6 5h14v1h-14zM22 5h3v1h-3zM0 6h4v1h-4zM5 6h16v1h-16zM22 6h4v1h-4zM0 7h3v1h-3zM5 7h16v1h-16zM23 7h3v1h-3zM0 8h3v1h-3zM4 8h5v1h-5zM11 8h4v1h-4zM17 8h5v1h-5zM23 8h3v1h-3zM0 9h3v1h-3zM4 9h5v1h-5zM11 9h4v1h-4zM17 9h5v1h-5zM23 9h3v1h-3zM1 10h2v1h-2zM4 10h5v1h-5zM11 10h4v1h-4zM17 10h5v1h-5zM23 10h2v1h-2zM2 11h1v1h-1zM4 11h18v1h-18zM23 11h1v1h-1zM4 12h18v1h-18zM4 13h18v1h-18zM4 14h1v1h-1zM21 14h1v1h-1zM5 15h1v1h-1zM20 15h1v1h-1zM5 16h16v1h-16zM6 17h1v1h-1zM19 17h1v1h-1zM6 18h3v1h-3zM17 18h2v1h-2zM5 19h2v1h-2zM9 19h8v1h-8zM4 20h2v1h-2z" />
    </svg>
  );
}

/** The centered page column. `narrow` for lists, `form` for single forms, `wide` to use the whole window (diffs, docs). */
export function Page({ narrow, form, wide, children }: { narrow?: boolean; form?: boolean; wide?: boolean; children: ReactNode }) {
  return <div className={cn("mx-auto max-w-[1280px] px-8 pb-12 max-md:px-4", narrow && "max-w-[1012px]", form && "max-w-[800px]", wide && "max-w-none px-6 max-md:px-4")}>{children}</div>;
}

export function PageHead({ title, children, actions }: { title: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-4 mt-6 flex flex-wrap items-center gap-4 [&+*]:mt-0!">
      <div className="min-w-0 flex-1">
        <h1 className="text-2xl font-normal">{title}</h1>
        {children && <p className="mt-1 text-muted-foreground">{children}</p>}
      </div>
      {actions}
    </div>
  );
}

/** A row of big numbers, as on a GitHub insights page. */
export function StatsRow({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("mt-4 flex flex-wrap rounded-md border", className)}>{children}</div>;
}

export function Stat({ n, label, icon }: { n: ReactNode; label: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-[1_1_160px] items-center gap-3 border-l border-border-muted px-4 py-3 first:border-l-0">
      {icon && <div className="inline-flex text-muted-foreground">{icon}</div>}
      <div>
        <div className="text-xl font-semibold leading-tight tabular-nums">{n}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

/** Label/value pairs. Values sit right-aligned, as in a sidebar, unless `start`. Children are <dt>/<dd>. */
export function DescriptionList({ start, className, children }: { start?: boolean; className?: string; children: ReactNode }) {
  return (
    <dl
      className={cn(
        "grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1.5 text-xs [&_dd]:min-w-0 [&_dt]:text-muted-foreground",
        start ? "[&_dd]:justify-self-start [&_dd]:wrap-anywhere" : "[&_dd]:justify-self-end",
        className,
      )}
    >
      {children}
    </dl>
  );
}

/** Preformatted output: logs, commands, file contents. */
export function Pre({ className, children }: { className?: string; children: ReactNode }) {
  return <pre className={cn("mt-2 overflow-auto rounded-md bg-muted p-4 font-mono text-xs leading-[1.45]", className)}>{children}</pre>;
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="grid gap-3 p-4" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className={(i + 1) % 3 === 0 ? "w-[54%]" : (i + 1) % 2 === 0 ? "w-[72%]" : undefined} />
      ))}
    </div>
  );
}

/** Primer's Blankslate. `bee` swaps the icon for the bee bee on the main empty pages. */
export function Empty({ icon: Icon = InboxIcon, bee, spin, children }: { icon?: OcticonIcon; bee?: BeeState; spin?: boolean; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-4 py-8 text-center text-muted-foreground [&_code]:rounded-md [&_code]:bg-neutral-muted [&_code]:px-1.5 [&_code]:py-0.5">
      {bee ? <BeeMascot state={bee} cell={3} className="mb-3" /> : <Icon size={24} className={cn("mb-2", spin && "animate-spin")} />}
      <div>{children}</div>
    </div>
  );
}

export function ErrorMessage({ error }: { error: unknown }) {
  const e = error instanceof HttpError ? error.body : { code: "ERROR", message: String((error as Error)?.message ?? error), hint: "" };
  const unauthorized = error instanceof HttpError && error.status === 401;
  return (
    <Alert variant="error">
      <AlertTitle>
        <AlertIcon /> {e.code}
      </AlertTitle>
      <div>{e.message}</div>
      {e.hint && <div className="text-xs text-muted-foreground">{e.hint}</div>}
      {unauthorized && (
        <div className="mt-2">
          <Link to="/login">Sign in with a token</Link>
        </div>
      )}
    </Alert>
  );
}

/** Shown instead of an admin-only page; `children` says what needs the admin action. */
export function AdminsOnly({ children }: { children: ReactNode }) {
  return (
    <Page narrow>
      <Alert variant="warning" className="mt-6">
        <AlertTitle>
          <LockIcon /> Admins only
        </AlertTitle>
        {children}
        <div className="mt-2">
          <Link to="/login">Sign in with an admin token</Link>
        </div>
      </Alert>
    </Page>
  );
}

/** Render loading / error / content for a query. */
export function Query<T>({ q, children }: { q: { isLoading: boolean; error: unknown; data: T | undefined }; children: (data: T) => ReactNode }) {
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorMessage error={q.error} />;
  if (q.data === undefined) return null;
  return <>{children(q.data)}</>;
}
